import React from 'react';
import { Cpu } from 'lucide-react';
import { ChannelTile } from '../ChannelTile';
import { PanelSection } from '../ui/PanelSection';
import { TargetSheet } from '../sheets/TargetSheet';
import { LoadSheet } from '../sheets/LoadSheet';
import type { ChannelTarget } from '../../../lib/rcu/query/targets';
import { allTargets } from '../../../lib/rcu/query/targets';
import { CHANNEL_KIND } from '../../../lib/rcu/catalog/channel';
import type { WiredLoad } from '../../../lib/rcu/model';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';

/** The RCU's onboard relay outputs, as a grid of tiles. */
export const OutputsPanel: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const [openTarget, setOpenTarget] = React.useState<ChannelTarget | null>(null);
  const [editing, setEditing] = React.useState<{ load: WiredLoad | null; target: ChannelTarget } | null>(
    null,
  );

  const outputs = allTargets(config).filter((target) => target.ref.kind === CHANNEL_KIND.RCU);

  return (
    <div className="space-y-4">
      <PanelSection
        title="Onboard outputs"
        description={`${outputs.length} relay outputs on the RCU itself, each driving one switched load.`}
        action={
          <span className="flex items-center gap-2 text-xs text-[#666666]">
            <Cpu className="h-4 w-4" />
            Change the count in RCU details
          </span>
        }
      >
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {outputs.map((target) => (
            <li key={target.shortLabel}>
              <ChannelTile target={target} onSelect={setOpenTarget} />
            </li>
          ))}
        </ul>
      </PanelSection>

      {openTarget !== null && (
        <TargetSheet
          isOpen
          target={openTarget}
          onClose={() => setOpenTarget(null)}
          onEditLoad={(load, target) => {
            setOpenTarget(null);
            setEditing({ load, target });
          }}
        />
      )}

      {editing !== null && (
        <LoadSheet
          isOpen
          load={editing.load}
          channel={editing.target.ref}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default OutputsPanel;
